import { ScenarioHandler, BaseScenario, ScenarioResult, AssistantContext } from '../types';

/**
 * Building Scenario Handler
 *
 * Handelt vragen over gebouwen:
 * - "Hoeveel gebouwen heb ik?"
 * - "Welke gebouwen hebben de meeste units?"
 * - "Gebouwen per bedrijf"
 * - "Overzicht van @2000-EMIE-12"
 */
export class BuildingScenario implements ScenarioHandler {
  examples = [
    'Hoeveel gebouwen heb ik?',
    'Welke gebouwen hebben de meeste units?',
    'Gebouwen per bedrijf',
    'Overzicht van @2000-EMIE-12',
    'Alle gebouwen in Antwerpen',
  ];

  detect(input: string): BaseScenario {
    const normalized = input.toLowerCase();

    const buildingKeywords = [
      'gebouw',
      'gebouwen',
      'pand',
      'panden',
      'building',
      'vastgoed',
    ];

    const hasBuildingKeyword = buildingKeywords.some(kw => normalized.includes(kw));
    const hasBuildingId = /\b\d{4}-[A-Z]+-\d{2}\b/i.test(input);

    // Exclusions: if it's clearly about tickets
    if (normalized.includes('ticket') || normalized.includes('probleem') || normalized.includes('defect')) {
      return { type: 'unknown', confidence: 0 };
    }

    if (hasBuildingKeyword || hasBuildingId) {
      return { type: 'building-analytics', confidence: 0.7 };
    }

    return { type: 'unknown', confidence: 0 };
  }

  async parse(input: string, _context: AssistantContext): Promise<ScenarioResult> {
    const normalized = input.toLowerCase();

    // Extract building ID if present
    const buildingMatch = input.match(/\b(\d{4}-[A-Z]+-\d{2})\b/i);
    const buildingId = buildingMatch ? buildingMatch[1].toUpperCase() : null;

    // Determine query type
    let queryType = 'overview';
    let chartType: 'bar' | 'line' | 'pie' | 'table' = 'table';
    let title = 'Gebouw overzicht';

    if (normalized.includes('hoeveel')) {
      queryType = 'count';
      title = 'Aantal gebouwen';
    } else if (normalized.includes('meeste units') || normalized.includes('per unit')) {
      queryType = 'by-unit-count';
      chartType = 'bar';
      title = 'Gebouwen per aantal units';
    } else if (normalized.includes('per bedrijf') || normalized.includes('per company')) {
      queryType = 'by-company';
      chartType = 'pie';
      title = 'Gebouwen per bedrijf';
    } else if (buildingId) {
      queryType = 'detail';
      title = `Details voor ${buildingId}`;
    }

    return {
      scenario: 'building-analytics',
      action: 'analyze',
      data: {
        queryType,
        buildingId,
      },
      chartConfig: {
        type: chartType,
        title,
        data: [],
      }
    };
  }

  async execute(result: ScenarioResult, _context: AssistantContext): Promise<any> {
    const { queryType, buildingId } = result.data;

    // Placeholder: deze endpoints moeten nog gemaakt worden in de backend
    // Voor nu simuleren we de data

    switch (queryType) {
      case 'count':
        return {
          ...result.chartConfig,
          data: [{ label: 'Totaal gebouwen', count: 42 }],
        };

      case 'by-unit-count':
        return {
          ...result.chartConfig,
          data: [
            { buildingId: '2000-EMIE-12', count: 45 },
            { buildingId: '2000-KANT-01', count: 38 },
            { buildingId: '2000-MEIR-05', count: 32 },
          ],
        };

      case 'by-company':
        return {
          ...result.chartConfig,
          data: [
            { company: 'Bedrijf A', count: 15 },
            { company: 'Bedrijf B', count: 12 },
            { company: 'Bedrijf C', count: 10 },
            { company: 'Overige', count: 5 },
          ],
        };

      case 'detail':
        // Zou building details ophalen via API
        return {
          ...result.chartConfig,
          data: [{
            buildingId,
            name: 'Emiel Banningstraat 12',
            address: 'Emiel Banningstraat 12, 2000 Antwerpen',
            units: 45,
            company: 'Bedrijf A',
          }],
        };

      default:
        // Overview: alle gebouwen
        return {
          ...result.chartConfig,
          data: [
            { buildingId: '2000-EMIE-12', units: 45, company: 'Bedrijf A' },
            { buildingId: '2000-KANT-01', units: 38, company: 'Bedrijf B' },
            { buildingId: '2000-MEIR-05', units: 32, company: 'Bedrijf C' },
          ],
        };
    }
  }
}
